package com.crowpay.views.screens.contractor

import com.crowpay.AddUserToMembership
import com.crowpay.Address
import com.crowpay.HasDisplayName
import com.crowpay.MemberRole
import com.crowpay.SharedUtils
import com.crowpay.User
import com.crowpay.UserMembership
import com.crowpay.UserRole
import com.crowpay.views.components.nav.AppDimensions
import com.crowpay.contractor
import com.crowpay.crowPayWarning
import com.crowpay.email
import com.crowpay.extensions.fullName
import com.crowpay.membership
import com.crowpay.role
import com.crowpay.sdk.notNullSession
import com.crowpay.selectedContractor
import com.crowpay.utils.Action
import com.crowpay.utils.asString
import com.crowpay.utils.existsDefaultFalse
import com.crowpay.utils.flatten
import com.crowpay.utils.modify2
import com.crowpay.utils.navigateTo
import com.crowpay.utils.pushChanges
import com.crowpay.utils.validation.Validator
import com.crowpay.views.components.nav.viewIsNarrow
import com.crowpay.views.components.nav.viewIsWide
import com.crowpay.views.components.requiredField
import com.crowpay.views.dialogs.GenericConfirmationDialog
import com.crowpay.views.screens.client.ClientForm
import com.crowpay.views.screens.common.DialogWithDialog
import com.crowpay.views.screens.common.secondDialog
import com.crowpay.views.theming.AppColors
import com.crowpay.views.theming.DangerButtonSemantic
import com.crowpay.views.theming.RemoveOutline
import com.crowpay.views.theming.TabSemantics
import com.crowpay.views.theming.body
import com.crowpay.views.theming.bodyBold
import com.crowpay.views.theming.dangerButton
import com.crowpay.views.theming.primaryButton
import com.crowpay.views.theming.secondaryButton
import com.crowpay.views.theming.section
import com.crowpay.views.theming.smallBody
import com.crowpay.views.theming.specCenteredText
import com.crowpay.views.theming.tertiaryButton
import com.crowpay.views.theming.title
import com.lightningkite.EmailAddress
import com.lightningkite.PhoneNumber
import com.lightningkite.UUID
import com.lightningkite.kiteui.Routable
import com.lightningkite.kiteui.models.Icon
import com.lightningkite.kiteui.models.rem
import com.lightningkite.kiteui.navigation.Page
import com.lightningkite.kiteui.navigation.dialogScreenNavigator
import com.lightningkite.kiteui.navigation.render
import com.lightningkite.kiteui.reactive.Constant
import com.lightningkite.kiteui.reactive.Draft
import com.lightningkite.kiteui.reactive.Property
import com.lightningkite.kiteui.reactive.Readable
import com.lightningkite.kiteui.reactive.bind
import com.lightningkite.kiteui.reactive.equalTo
import com.lightningkite.kiteui.reactive.invoke
import com.lightningkite.kiteui.reactive.shared
import com.lightningkite.kiteui.views.RView
import com.lightningkite.kiteui.views.ViewModifiable
import com.lightningkite.kiteui.views.ViewWriter
import com.lightningkite.kiteui.views.atEnd
import com.lightningkite.kiteui.views.atStart
import com.lightningkite.kiteui.views.centered
import com.lightningkite.kiteui.views.compact
import com.lightningkite.kiteui.views.dialog
import com.lightningkite.kiteui.views.direct.DismissBackground
import com.lightningkite.kiteui.views.direct.button
import com.lightningkite.kiteui.views.direct.col
import com.lightningkite.kiteui.views.direct.onClick
import com.lightningkite.kiteui.views.direct.onlyWhen
import com.lightningkite.kiteui.views.direct.radioToggleButton
import com.lightningkite.kiteui.views.direct.row
import com.lightningkite.kiteui.views.direct.rowCollapsingToColumn
import com.lightningkite.kiteui.views.direct.scrolls
import com.lightningkite.kiteui.views.direct.select
import com.lightningkite.kiteui.views.direct.sizeConstraints
import com.lightningkite.kiteui.views.direct.space
import com.lightningkite.kiteui.views.direct.stack
import com.lightningkite.kiteui.views.direct.swapView
import com.lightningkite.kiteui.views.direct.swapping
import com.lightningkite.kiteui.views.direct.textInput
import com.lightningkite.kiteui.views.expanding
import com.lightningkite.kiteui.views.fieldTheme
import com.lightningkite.kiteui.views.forEachUpdating
import com.lightningkite.kiteui.views.l2.icon
import com.lightningkite.kiteui.views.warning
import com.lightningkite.lightningdb.Query
import com.lightningkite.lightningdb.condition
import com.lightningkite.lightningdb.eq
import com.lightningkite.serialization.lensPath
import com.lightningkite.serialization.notNull

@Routable("contractor/users")
class ManageContractorUsers : Page {
    override val title: Readable<String> = Constant("Manage Users")

    val membership = shared { notNullSession().self().membership ?: UserMembership(SharedUtils.nullUUID, MemberRole.Employee) }

    private val userComparator = compareBy<User> { it.membership?.role }.thenBy { it.email }
    val usersRaw = shared {
        val session = notNullSession()
        val contractor = selectedContractor() ?: return@shared emptyList()
        session.users.query(
            Query(
                condition { it.membership.notNull.contractor eq contractor },
            )
        )().sortedWith(userComparator)
    }

    private val cache = HashMap<UUID, Draft<User>>()
    val users = shared { usersRaw().map { cache.getOrPut(it._id) { Draft(it) } } }

    private fun ViewWriter.addUsersButton() = button {
        existsDefaultFalse { membership().role == MemberRole.Admin }
        specCenteredText("+ Add User")
        onClick {
            dialogScreenNavigator.navigate(AddUserDialog(membership()))
        }
    }

    private fun ViewWriter.renderUserSettings() = col {
        forEachUpdating(users) { unflattened ->
            val user = unflattened.flatten()
            val userRole = shared { unflattened().published().membership?.role ?: MemberRole.Employee }
            section - rowCollapsingToColumn(AppDimensions.wideCollapseWidth) {
                if (viewIsNarrow) compact - row {
                    centered - body { ::content { user().fullName } }
                    centered - expanding - smallBody {
                        wraps = false
                        ellipsis = true
                        ::content { user().email.raw }
                    }
                }
                else compact - expanding - col {
                    vertical = viewIsWide
                    body { ::content { user().fullName } }
                    smallBody { ::content { user().email.raw } }
                }
                row {
                    centered - sizeConstraints(width = 8.rem) - compact - fieldTheme - select {
                        ::enabled { userRole() < membership().role }

                        bind(
                            user.lensPath { it.membership.notNull.role },
                            shared { MemberRole.entries.filter { it <= membership().role } }
                        ) { it.name }
                    }
                    centered - dangerButton - button {
                        existsDefaultFalse { membership().role.let { it == MemberRole.Admin && it > userRole() } }
                        specCenteredText("Remove User")
                        onClick("Remove user from account") {
                            val user = user()
                            dialogScreenNavigator navigateTo GenericConfirmationDialog(
                                "Remove User",
                                "Are you sure you want to remove ${user.fullName} from your account? This cannot be undone.",
                                confirmationText = "Remove ${user.fullName}",
                                confirmationTheme = DangerButtonSemantic
                            ) { confirmed ->
                                if (!confirmed) return@GenericConfirmationDialog

                                notNullSession().users[user._id].modify2 { it.membership assign null }
                            }
                        }
                    }
                }
            }
        }
    }

    fun save(writer: ViewWriter) = Action("Save Changes") {
        val changed = users().filter { it.changesMade() }
        if (changed.isEmpty()) return@Action

        writer.dialogScreenNavigator navigateTo GenericConfirmationDialog(
            if (changed.size > 1) "Save User Role Changes" else "Change User Role",
            "Confirm that you want to save these changes",
            confirmationText = "Save Changes"
        ) { confirmed ->
            if (confirmed) {
                val userCache = notNullSession().users
                changed.forEach { it.pushChanges(userCache) }
            }
        }
    }

    override fun ViewWriter.render2(): ViewModifiable = scrolls - stack {
        sizeConstraints(width = AppDimensions.pageWidth) - col {
            title("Users")

            atEnd - tertiaryButton - addUsersButton()

            renderUserSettings()

            atEnd - onlyWhen { users().any { it.changesMade() } } - row {
                secondaryButton - button {
                    specCenteredText("Discard")
                    onClick {
                        users().forEach { it.cancel() }
                    }
                }
                primaryButton - button {
                    specCenteredText("Save Changes")
                    action = save(this)
                }
            }
        }
    }

    fun renderTo(writer: ViewWriter) = with(writer) {
        col {
            atStart - tertiaryButton - addUsersButton()

            renderUserSettings()
        }
    }

    class AddUserDialog(val membership: UserMembership, startData: User? = null) : DialogWithDialog {
        enum class Type(override val displayName: String) : HasDisplayName {
            New("New User"),
            Existing("Existing User"),
        }
        val formType = Property(if (startData == null) Type.New else Type.Existing)

        private val userNotFound = Property(false)

        val user = Draft(
            startData ?: User(
                email = EmailAddress(""),
                phoneNumber = PhoneNumber(""),
                firstName = "",
                lastName = "",
                address = Address(),
                role = UserRole.Customer,
                membership = UserMembership(membership.contractor, MemberRole.Employee)
            )
        )

        private val formWidth = 50.rem

        val clientForm = ClientForm(user, formWidth, "Address")
        val existingValidator = Validator()

        private fun RView.roleSelector() = requiredField("Role") {
            select {
                bind(
                    user.lensPath { it.membership.notNull.role },
                    Constant(MemberRole.entries.filter { it <= membership.role })
                ) { it.name }
            }
        }

        private fun ViewWriter.newUser() = col {
            spacing = AppDimensions.majorColumnSpacing
            clientForm.render(this)
            roleSelector()
        }

        private fun ViewWriter.existingUser() = col {
            with (existingValidator) {
                onlyWhen { userNotFound() } - warning - col {
                    row {
                        icon(Icon.crowPayWarning, "Warning")
                        centered - bodyBold("User Not Found")
                    }
                    body("We could not find the user with the provided email. Please double check the email or create a new user.")
                }
                requiredField("User Email") {
                    val email = user.lensPath { it.email }.asString()
                    validate { email().matches(SharedUtils.emailPattern) } - textInput {
                        content bind email
                    }
                }
                roleSelector()
            }
        }

        override fun DismissBackground.renderDialog(): ViewModifiable =
            scrolls - centered - dialog - stack {
                spacing = AppDimensions.fullIndent
                RemoveOutline.onNext - col {
                    title("Add User")

                    TabSemantics(AppColors.grey.light2).onNext - row {
                        for (type in Type.entries) {
                            expanding - radioToggleButton {
                                specCenteredText(type.displayName)
                                checked bind formType.equalTo(type)
                            }
                        }
                    }

                    sizeConstraints(minHeight = 20.rem, width = formWidth) - swapView {
                        swapping(
                            current = { formType() },
                            views = {
                                when (it) {
                                    Type.Existing -> existingUser()
                                    Type.New -> newUser()
                                }
                            }
                        )
                    }

                    space(0.5)

                    atEnd - row {
                        secondaryButton - button {
                            specCenteredText("Go Back")
                            onClick { dialogScreenNavigator.dismiss() }
                        }
                        primaryButton - button {
                            ::enabled {
                                when (formType()) {
                                    Type.Existing -> existingValidator.allValid()
                                    Type.New -> clientForm.allValid()
                                }
                            }
                            specCenteredText("Submit")
                            onClick("Submit") {
                                secondDialog.value = GenericConfirmationDialog(
                                    "Add User to Account",
                                    "Are you sure you want to add this user to your account?",
                                    confirmationText = "Add User",
                                    dismiss = { dismissSecondDialog() }
                                ) { confirmed ->
                                    if (!confirmed) return@GenericConfirmationDialog

                                    when (formType.value) {
                                        Type.Existing -> {
                                            val user = user()
                                            val session = notNullSession()
                                            val updated = session.nonCached.user.addUserToMembership(
                                                AddUserToMembership(user.email, user.membership?.role ?: MemberRole.Employee)
                                            )
                                            if (updated == null) userNotFound.value = true
                                            else {
                                                userNotFound.value = false
                                                session.users.localSignalUpdate(
                                                    matching = { it._id == updated },
                                                    modify = { updated }
                                                )
                                            }
                                        }

                                        Type.New -> user.pushChanges(notNullSession().users)
                                    }

                                    if (userNotFound.value) return@GenericConfirmationDialog

                                    dialogScreenNavigator.dismiss()
                                }
                            }
                        }
                    }
                }
            }
        }

}